import { useEffect, useState } from "react";

export type AsyncState<T> =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "success"; data: T }
  | { status: "error"; error: string };

export const unpackAsync = <T>(
  state: AsyncState<T>,
): { data: T | undefined; loading: boolean; error: string | undefined } => ({
  data: state.status === "success" ? state.data : undefined,
  loading: state.status === "loading",
  error: state.status === "error" ? state.error : undefined,
});

// Project the `success` payload through `fn`; idle/loading/error pass through
// unchanged. Used to feed connector-shaped data into a shared display state.
export const mapAsync = <T, U>(
  state: AsyncState<T>,
  fn: (data: T) => U,
): AsyncState<U> => {
  if (state.status === "success") {
    return { status: "success", data: fn(state.data) };
  }
  return state;
};

type Listener<T> = (state: AsyncState<T>) => void;

interface CacheEntry<T> {
  state: AsyncState<T>;
  listeners: Set<Listener<T>>;
  inflight: Promise<void> | null;
}

export interface KeyedAsyncCache<Args extends readonly unknown[], T> {
  use(args: Args): { state: AsyncState<T>; ensureLoaded: () => void };
  /** Synchronous read; does not subscribe. */
  peek(args: Args): AsyncState<T>;
  invalidate(predicate: (key: string) => boolean): void;
  __resetForTests(): void;
}

interface Options<Args extends readonly unknown[], T> {
  keyFn: (...args: Args) => string;
  fetcher: (...args: Args) => Promise<T>;
  fallbackError: string;
}

export function createKeyedAsyncCache<Args extends readonly unknown[], T>({
  keyFn,
  fetcher,
  fallbackError,
}: Options<Args, T>): KeyedAsyncCache<Args, T> {
  const cache = new Map<string, CacheEntry<T>>();

  const ensureEntry = (key: string): CacheEntry<T> => {
    let entry = cache.get(key);
    if (!entry) {
      entry = {
        state: { status: "idle" },
        listeners: new Set(),
        inflight: null,
      };
      cache.set(key, entry);
    }
    return entry;
  };

  const setState = (entry: CacheEntry<T>, state: AsyncState<T>): void => {
    entry.state = state;
    for (const listener of entry.listeners) listener(state);
  };

  const fetchInto = (entry: CacheEntry<T>, args: Args): Promise<void> => {
    if (entry.inflight) return entry.inflight;
    setState(entry, { status: "loading" });
    const p = fetcher(...args)
      .then((data) => {
        setState(entry, { status: "success", data });
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : fallbackError;
        setState(entry, { status: "error", error: message });
      })
      .finally(() => {
        entry.inflight = null;
      });
    entry.inflight = p;
    return p;
  };

  return {
    use(args: Args) {
      const key = keyFn(...args);
      const [state, setLocal] = useState<AsyncState<T>>(
        () => ensureEntry(key).state,
      );

      useEffect(() => {
        const entry = ensureEntry(key);
        setLocal(entry.state);
        entry.listeners.add(setLocal);
        return () => {
          entry.listeners.delete(setLocal);
        };
      }, [key]);

      const ensureLoaded = (): void => {
        const entry = ensureEntry(key);
        if (entry.state.status === "idle" || entry.state.status === "error") {
          void fetchInto(entry, args);
        }
      };

      return { state, ensureLoaded };
    },

    peek(args) {
      const key = keyFn(...args);
      return ensureEntry(key).state;
    },

    invalidate(predicate) {
      for (const key of [...cache.keys()]) {
        if (predicate(key)) cache.delete(key);
      }
    },

    __resetForTests() {
      cache.clear();
    },
  };
}
